// React
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useActiveAuthProvider } from '@refinedev/core';

// Mantine
import {
  Box,
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Anchor,
  useComputedColorScheme,
  Flex,
} from '@mantine/core';
import { useForm } from '@mantine/form';

// Sentry
import * as Sentry from '@sentry/react';

// Utils
import { validateEmail } from 'utils/validate';
import { getLoginCardBackground } from 'utils/styleUtils';
import { nowISO, parseDateTime } from 'utils/dateUtils';

import { useIsMobile } from 'hooks';
import {
  IconCancel,
  IconCheck,
  IconMail,
  IconArrowRight,
  IconLoader2,
  IconShieldCheck,
  IconFingerprint,
} from '@tabler/icons-react';

// Components
import { QDocLogo } from 'components/common/QDocLogo';

export const ForgotPassword: React.FC = () => {
  const title = 'Forgot Password';
  useDocumentTitle(`${title} | QBilling`);
  const computedColorScheme = useComputedColorScheme('dark');
  const backgroundImage = `url(/background-${computedColorScheme}.svg)`;
  const navigate = useNavigate();
  const authProvider = useActiveAuthProvider();
  const isMobile = useIsMobile(500);
  const [inputError, setInputError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmailInput = (value: string) => {
    if (!validateEmail(value)) {
      return 'Please enter a valid email address';
    }
    if (inputError) {
      return 'User not found';
    }
    return null;
  };

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: validateEmailInput,
    },
  });

  const handleForgotPassword = async (values: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = (await authProvider.forgotPassword(values)) as {
        type: string;
        success: boolean;
      };
      switch (response.type) {
        case 'resetPassword':
          localStorage.setItem('email', values.email);
          setSuccess(true);
          navigate('/update-password');
          break;
        case 'onError':
          setError('Unable to process request');
          form.setErrors({ email: 'Unable to process request' });
          break;
      }
    } catch (e: any) {
      if (e.name === 'UserNotFoundException') {
        setInputError(true);
        setError('User not found');
        form.setErrors({ email: 'User not found' });
      }
      Sentry.captureException(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        backgroundImage: backgroundImage,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
      }}
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{
          height: '100%'
        }}
      >
        <Card
          withBorder
          radius="xl"
          p="lg"
          style={{
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            background: getLoginCardBackground(computedColorScheme),
            borderColor: 'rgba(255,255,255,0.18)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            transition: 'transform 160ms ease',
          }}
        >
          <Group align="center" gap="md" mt="lg" mb="xl">
            <QDocLogo />
            <div>
              <Title order={3}>{title}</Title>
              <Text size="sm" c="dimmed">
                Enter your email to reset your password
              </Text>
            </div>
          </Group>

          <form
            data-testid="forgot-password-form"
            onSubmit={form.onSubmit((values) => handleForgotPassword(values))}
          >
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                <Group gap={6}>
                  <IconMail size={16} /> Email
                </Group>
              </Text>
              <Box pos="relative">
                <TextInput
                  placeholder="you@company.com"
                  size="md"
                  radius="lg"
                  leftSection={<IconMail size={16} />}
                  rightSection={<IconShieldCheck size={16} opacity={0.6} />}
                  autoComplete="email"
                  key={form.key('email')}
                  {...form.getInputProps('email')}
                />
              </Box>
              {error && (
                <Card
                  withBorder
                  radius="md"
                  p="sm"
                  style={{
                    background:
                      computedColorScheme === 'dark' ? 'rgba(239,68,68,0.12)' : '#fee2e2',
                    borderColor: 'rgba(239,68,68,0.35)',
                  }}
                >
                  <Text size="sm" c={computedColorScheme === 'dark' ? 'red.3' : 'red.7'}>
                    {error}
                  </Text>
                </Card>
              )}
              <Button
                type="submit"
                size="md"
                radius="lg"
                disabled={loading || success}
                leftSection={
                  loading ? (
                    <IconLoader2 size={18} className="spin" />
                  ) : success ? (
                    <IconCheck size={18} />
                  ) : (
                    <IconArrowRight size={18} />
                  )
                }
                c={computedColorScheme === 'dark' ? 'white' : 'black'}
                style={{
                  background: 'linear-gradient(to right, #22d3ee, #34d399)',
                  boxShadow: '0 4px 25px rgba(52,211,153,0.25)',
                }}
                loading={loading}
              >
                {loading
                  ? 'Sending…'
                  : success
                  ? 'Email Sent'
                  : 'Send Reset Link'}
              </Button>
              <Button
                variant="light"
                radius="lg"
                onClick={() => navigate('/login')}
                c={computedColorScheme === 'dark' ? 'white' : 'black'}
              >
                {isMobile ? <IconCancel /> : 'Back to login'}
              </Button>

              <Divider label="Security" labelPosition="center" my="xs" />

              <Group justify="space-between" mt="xs">
                <Group gap={6}>
                  <IconFingerprint size={16} />
                  <Text size="xs" c="dimmed">
                    Enterprise-grade security
                  </Text>
                </Group>
                <Group gap="xs">
                  <Anchor
                    size="xs"
                    href="https://qdoc.ca/terms-and-conditions/"
                  >
                    Privacy
                  </Anchor>
                  <Anchor
                    size="xs"
                    href="https://qdoc.ca/terms-and-conditions/"
                  >
                    Terms
                  </Anchor>
                  <Anchor size="xs" href="https://qdoc.ca/contact-us/">
                    Help
                  </Anchor>
                </Group>
              </Group>
            </Stack>
          </form>
        </Card>
        <Text ta="center" mt="xl" size="xs" c="dimmed">
          © {parseDateTime(nowISO()).year} QDoc Inc. All rights reserved.
        </Text>
      </Flex>
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
};
